import { useEffect, useState } from "react"
import { useStore } from "stores/utils/use-store"
import { Button, Form, InputNumber, Typography } from "antd"
import { observer } from "mobx-react"
import { notifications } from "notifications"
import { VariousConfigType } from "domains/managingJson/store/types"
const { Title, Text } = Typography


const TimezoneHelper = ({ centralTime, suffix = "" }: { centralTime: string; suffix?: string }) => (
  centralTime ? (
    <div style={{ marginTop: -16, marginBottom: 16 }}>
      <Text type="secondary" style={{ fontSize: "12px" }}>
        This corresponds to {centralTime}{suffix} US Central Time
      </Text>
    </div>
  ) : null
)


export const VariousConfig = observer(() => {
  const { dataStore: { managingJsonStore } } = useStore()
  const [data, setData] = useState<VariousConfigType | undefined>()
  const [form] = Form.useForm<VariousConfigType>()
  const [startHourCentral, setStartHourCentral] = useState<string>("")
  const [endHourCentral, setEndHourCentral] = useState<string>("")

  const convertUTCHourToCentral = (utcHour: number | undefined) => {
    if (utcHour === undefined || utcHour < 0 || utcHour > 23) {
      return ""
    }

    const utcDate = new Date()
    utcDate.setUTCHours(utcHour, 0, 0, 0)

    // Convert to Central Time (handles both CST and CDT automatically)
    const centralHour = utcDate.toLocaleString("en-US", {
      timeZone: "America/Chicago",
      hour: "2-digit",
      hour12: false,
    })

    return centralHour
  }

  useEffect(() => {
    managingJsonStore.getData()
  }, [managingJsonStore])

  useEffect(() => {
    const mobileConfigData = managingJsonStore?.itemTypes?.find(
      item => item.type === "variousConfig",
    )
    if (mobileConfigData) {
      const data = mobileConfigData.data as VariousConfigType
      setData(data)
      form.setFieldsValue(data)
      if (data.tropicanaNightperiodStartHour !== undefined) {
        setStartHourCentral(convertUTCHourToCentral(data.tropicanaNightperiodStartHour))
      }
      if (data.tropicanaNightperiodEndHour !== undefined) {
        setEndHourCentral(convertUTCHourToCentral(data.tropicanaNightperiodEndHour))
      }
    }
  }, [form, managingJsonStore.itemTypes])

  const onFinishHandler = async() => {
    const {
      codStartgameAutoclose,
      codLaunchgameAutoclose,
      codEndgameAutoclose,
      codGameSummaryAutoclose,
      launchgameAutoclose,
      tropicanaTrackingResetTimeHour,
      tropicanaNightperiodStartHour,
      tropicanaNightperiodEndHour,
    } = form.getFieldsValue()

    managingJsonStore.funcAfterSuccess = async() => {
      notifications.success("Saved successfully")
      await managingJsonStore.getData()
    }

    const config = {
      name: "variousConfig",
      type: "variousConfig",
      data: {
        codStartgameAutoclose,
        codLaunchgameAutoclose,
        codEndgameAutoclose,
        codGameSummaryAutoclose,
        launchgameAutoclose,
        tropicanaTrackingResetTimeHour,
        tropicanaNightperiodStartHour,
        tropicanaNightperiodEndHour,
      },
    } as const

    data ? await managingJsonStore.update(config) : await managingJsonStore.create(config)
  }

  return (
    <Form onFinish={onFinishHandler} form={form}>
      <Title level={2}>Various Config</Title>
      <Form.Item
        name="codStartgameAutoclose"
        label="COD games startgame window autoclose (seconds)"
      >
        <InputNumber />
      </Form.Item>
      <Form.Item
        name="codLaunchgameAutoclose"
        label="COD games launchgame window autoclose (seconds)"
      >
        <InputNumber />
      </Form.Item>
      <Form.Item
        name="codEndgameAutoclose"
        label="COD games endgame window autoclose (seconds)"
      >
        <InputNumber />
      </Form.Item>
      <Form.Item
        name="codGameSummaryAutoclose"
        label="COD games gamesummary window autoclose (seconds)"
      >
        <InputNumber />
      </Form.Item>
      <Form.Item
        name="launchgameAutoclose"
        label="ALL launch screens hard autoclose (seconds)"
        help="Empty = client default 20s. Set 0 to disable the auto-close entirely."
      >
        <InputNumber min={0} precision={0} />
      </Form.Item>
      <Form.Item
        name="tropicanaTrackingResetTimeHour"
        label="Tropicana tracking reset time hour (US Central Time)"
        help="Setting it to 2 will reset the tracking at 2 AM US Central Time"
      >
        <InputNumber
          min={0}
          max={23}
          placeholder="0-23"
        />
      </Form.Item>

      <Form.Item
        name="tropicanaNightperiodStartHour"
        label="Tropicana Night Period Start Hour (US Central Time)"
      >
        <InputNumber
          min={0}
          max={23}
          placeholder="0-23"
          onChange={value => {
            setStartHourCentral(convertUTCHourToCentral(value ?? undefined))
          }}
        />
      </Form.Item>
      <TimezoneHelper centralTime={startHourCentral} suffix=":00" />

      <Form.Item
        name="tropicanaNightperiodEndHour"
        label="Tropicana Night Period End Hour (US Central Time)"
      >
        <InputNumber
          min={0}
          max={23}
          placeholder="0-23"
          onChange={value => {
            setEndHourCentral(convertUTCHourToCentral(value ?? undefined))
          }}
        />
      </Form.Item>
      <TimezoneHelper centralTime={endHourCentral} suffix=":00" />

      <Button type="primary" htmlType="submit">
        Save
      </Button>

    </Form>
  )
})
